/**
 * /growth/ref-links — list. Cycle 15.
 *
 * Shareable m.me/ig.me deep links. Each row shows the IG account, the
 * generated URL with copy + QR download, and a hit counter.
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { api } from "../../../../lib/api.js";
import { useAuth } from "../../../../lib/auth-context.js";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

interface RefLinkRow {
  id: string;
  name: string;
  description: string | null;
  payload: string;
  enabled: boolean;
  hits: number;
  flowId: string | null;
  tagsToAdd: string[];
  igAccountId: string;
  igUsername: string;
  url: string;
  createdAt: string;
  updatedAt: string;
}

export const Route = createFileRoute("/_app/growth/ref-links/")({
  component: RefLinksList,
});

function RefLinksList() {
  const { activeIgAccount } = useAuth();
  const [rows, setRows] = useState<RefLinkRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (!activeIgAccount) return;
    void load();
  }, [activeIgAccount?.id]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ ig_account_id: activeIgAccount!.id });
      const r = await api.get<{ refLinks: RefLinkRow[] }>(`/ref-links?${params}`);
      setRows(r.data.refLinks);
    } catch {
      setError("Failed to load ref links");
    } finally {
      setLoading(false);
    }
  }

  async function remove(id: string) {
    if (!confirm("Delete this ref link?")) return;
    try {
      await api.delete(`/ref-links/${id}`);
      setRows((cur) => cur.filter((r) => r.id !== id));
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })
        .response?.data?.message;
      setError(msg ?? "Failed to delete");
    }
  }

  async function copyUrl(row: RefLinkRow) {
    try {
      await navigator.clipboard.writeText(row.url);
      setCopiedId(row.id);
      setTimeout(() => setCopiedId((c) => (c === row.id ? null : c)), 1500);
    } catch {
      // Clipboard API may be unavailable; ignore.
    }
  }

  return (
    <section style={{ maxWidth: 880, margin: "0 auto" }}>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 28 }}>Ref links</h1>
        <Link to="/growth/ref-links/new" className="brut-button">
          + New ref link
        </Link>
      </header>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 24 }}>
        Shareable links that DM the bot pre-configured — tag the contact, start a
        flow, attribute the source. Drop the QR code on print materials.
      </p>

      {error ? (
        <div
          className="brut-card"
          style={{ background: "#ffd7d7", padding: 10, marginBottom: 12 }}
        >
          {error}
        </div>
      ) : null}

      {loading ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <div className="brut-card" style={{ textAlign: "center", padding: 24 }}>
          <p style={{ margin: "0 0 8px", fontWeight: 700 }}>No ref links yet</p>
          <p style={{ margin: 0, fontSize: 14, color: "var(--muted)" }}>
            Ref links are <code>ig.me/m/&lt;handle&gt;?ref=&lt;payload&gt;</code> URLs
            (with QR codes) that pre-tag the contact + auto-launch a flow
            when someone first DMs you. Great for swipe-up campaigns.
          </p>
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
          {rows.map((r) => (
            <li
              key={r.id}
              className="brut-card"
              style={{ padding: 14, display: "grid", gap: 8 }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <Link
                    to="/growth/ref-links/$refLinkId"
                    params={{ refLinkId: r.id }}
                    style={{ fontWeight: 800, fontSize: 16, textDecoration: "none" }}
                  >
                    {r.name}
                  </Link>
                  <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
                    @{r.igUsername} · {r.hits} hit{r.hits === 1 ? "" : "s"}
                    {r.enabled ? "" : " · disabled"}
                  </div>
                </div>
                <button
                  onClick={() => void remove(r.id)}
                  className="brut-button is-danger"
                  style={{ fontSize: 13, padding: "6px 10px" }}
                >
                  Delete
                </button>
              </div>
              <code
                style={{
                  background: "var(--bg-soft)",
                  padding: "6px 8px",
                  fontSize: 12,
                  overflow: "auto",
                }}
              >
                {r.url}
              </code>
              <div style={{ display: "flex", gap: 8, fontSize: 13 }}>
                <button
                  onClick={() => void copyUrl(r)}
                  className="brut-button is-secondary"
                  style={{ padding: "4px 10px", fontSize: 12 }}
                >
                  {copiedId === r.id ? "Copied!" : "Copy URL"}
                </button>
                <a
                  href={`${API_BASE}/ref-links/${r.id}/qr`}
                  className="brut-button is-secondary"
                  style={{ padding: "4px 10px", fontSize: 12, textDecoration: "none" }}
                >
                  QR PNG
                </a>
                <a
                  href={`${API_BASE}/ref-links/${r.id}/qr?format=svg`}
                  className="brut-button is-secondary"
                  style={{ padding: "4px 10px", fontSize: 12, textDecoration: "none" }}
                >
                  QR SVG
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
